import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AuthProvider, useAuth } from './providers/AuthProvider';
import { WebSocketProvider } from './providers/WebSocketProvider';
import { useSoftphone } from './hooks/useSoftphone';
import { Sidebar } from './components/layout/Sidebar';
import { Header } from './components/layout/Header';
import { PortalHeader } from './components/layout/PortalHeader';
import { BrowserRouter, AppRoutes } from './router';
import { Toaster } from './components/ui/sonner';
import { InboundToast } from './components/layout/InboundToast';
import { InboundCallToast } from './components/layout/InboundCallToast';
import { RejoinPrompt } from './components/layout/RejoinPrompt';
import { TransferDialog } from './components/layout/TransferDialog';
import { InCallModal } from './components/voice/InCallModal';
import { InactivityWarningModal } from './components/layout/InactivityWarningModal';
import { TenantErrorPage } from './pages/TenantErrorPage';
import { init as initIdle, dispose as disposeIdle, getLastActivityAt } from './lib/idle';
import { installChunkErrorReload } from './lib/chunk-reload';

// Idle-watcher thresholds — kept on the client purely for the warning
// modal UX. The server-side cron sweep is the actual enforcer (15m
// auto-clock-out, 60m force-logout). All math in epoch-ms UTC.
const IDLE_WARNING_MS = 50 * 60 * 1_000;
const IDLE_LOGOUT_MS = 60 * 60 * 1_000;
const IDLE_TICK_MS = 1_000;

function AppShell() {
  const { user, isLoading, isAuthenticated, tenantError, portalView } = useAuth();

  // Portal view (agency owner, or platform-ops impersonating one): no rep
  // cockpit. The full Header, KPI surfaces, and the inbound-call widgets
  // stay unmounted. The softphone DOES mount (#1426) — portal calling is
  // outbound-only from the portal number set; inbound never routes to an
  // AO (apex-voice's routing gate only rings on-duty reps), so the
  // inbound toast/rejoin/transfer widgets have nothing to show.
  // `portalView` comes from AuthProvider so impersonation reproduces the
  // real portal-only shell.
  const portalOnly = portalView;

  // Initialize softphone after auth (voice WS + Twilio SDK). Skip when the
  // user has no tenant — the main shell isn't going to mount and every
  // voice call would fail anyway.
  useSoftphone(isAuthenticated && !tenantError);

  // Idle / input-activity tracker — wires the window listeners that
  // populate `getLastActivityAt()`. The WebSocketProvider's heartbeat
  // includes the value so the server-side cron sweep can fire 15m clock-
  // out and 60m force-logout thresholds. Init runs once when auth lands;
  // tear down on unauth so a logged-out tab stops emitting.
  const idleArmed = isAuthenticated && !tenantError;
  useEffect(() => {
    if (!idleArmed) return;
    initIdle();
    return () => disposeIdle();
  }, [idleArmed]);

  // Inactivity warning modal — purely UX. The 50-min threshold previews
  // the server's 60-min force-logout so the user has 10 min to react. Any
  // qualifying input clears it via the idle module's listeners; the
  // ticker re-evaluates each second and dismisses the modal when
  // `getLastActivityAt()` advances back inside the active window.
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [secondsUntilLogout, setSecondsUntilLogout] = useState(
    Math.ceil((IDLE_LOGOUT_MS - IDLE_WARNING_MS) / 1000),
  );

  useEffect(() => {
    if (!idleArmed) {
      setShowInactivityWarning(false);
      return;
    }
    const tick = () => {
      const idleMs = Date.now() - getLastActivityAt();
      if (idleMs >= IDLE_WARNING_MS) {
        setShowInactivityWarning(true);
        setSecondsUntilLogout(Math.max(0, Math.ceil((IDLE_LOGOUT_MS - idleMs) / 1000)));
      } else {
        setShowInactivityWarning(false);
      }
    };
    tick();
    const handle = window.setInterval(tick, IDLE_TICK_MS);
    return () => window.clearInterval(handle);
  }, [idleArmed]);

  const handleStayActive = useCallback(() => {
    // Clicking the button is itself a `click` event the idle module's
    // window listener already saw, so `getLastActivityAt()` is fresh by
    // the time this runs. Dismiss the modal immediately for instant UX
    // — the next tick will keep it dismissed.
    setShowInactivityWarning(false);
  }, []);

  // Surface dial failures from softphone.ts (#698). Every silent-return
  // path in dial() emits this event so the rep sees why their click didn't
  // place a call (wrong tab, no voice WS, apex-voice rejected, etc.).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ reason?: string }>).detail;
      const reason = detail?.reason ?? "Couldn't place the call.";
      toast.error(reason);
    };
    window.addEventListener('apex:dial-error', handler);
    return () => window.removeEventListener('apex:dial-error', handler);
  }, []);

  // Surface rolling-deploy version drift. Three independent detectors can
  // dispatch this event — apex-queue WS hello frame (WebSocketProvider),
  // apex-voice WS hello frame (voice-ws.ts), and the REST X-Build-Sha
  // header (lib/api.ts) — and each de-dupes within its own channel. We
  // dedupe across channels here so the user sees one toast no matter
  // which surface noticed first. The loaded SPA chunks (softphone, lazy
  // routes) won't exist on the new pod, so any route the user clicks
  // past this point may 404 until they refresh. Persistent toast with a
  // Refresh action; never auto-reload — that would kill an active call.
  useEffect(() => {
    let toasted = false;
    const handler = () => {
      if (toasted) return;
      toasted = true;
      toast('A new version is available.', {
        description: 'Refresh when you have a moment to keep things working smoothly.',
        duration: Infinity,
        action: {
          label: 'Refresh now',
          onClick: () => window.location.reload(),
        },
      });
    };
    window.addEventListener('apex:version-drift', handler);
    return () => window.removeEventListener('apex:version-drift', handler);
  }, []);

  if (isLoading) {
    return (
      <div className="size-full flex items-center justify-center bg-background">
        <div className="flex items-center gap-3 text-muted-foreground">
          <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // AuthProvider handles redirect
  }

  // Authenticated but no tenant → show the tenant-error page instead of
  // the main shell. Explicit branch so we never bounce the user back to
  // /auth/login from this state (that's what produced the redirect loop
  // this page exists to prevent).
  if (tenantError) {
    return <TenantErrorPage />;
  }

  return (
    <div className="h-screen w-screen flex bg-background font-sans overflow-hidden text-foreground">
      <Toaster position="bottom-right" theme="light" />
      {!portalOnly && (
        <>
          <InboundToast />
          <InboundCallToast />
          <RejoinPrompt />
          <TransferDialog />
        </>
      )}
      {/* In-call UI mounts for both shells — portal users place outbound
          calls from the message thread (#1426). */}
      <InCallModal />
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {portalOnly ? <PortalHeader user={user} /> : <Header user={user} />}

        <div className="flex-1 flex min-h-0 overflow-hidden">
          <AppRoutes />
        </div>
      </div>

      <InactivityWarningModal
        open={showInactivityWarning}
        secondsUntilLogout={secondsUntilLogout}
        onStayActive={handleStayActive}
      />
    </div>
  );
}

export function App() {
  // Hard-failure safety net for multi-pod rollouts (#1060). Backs up the
  // <LazyRouteErrorBoundary> in router.tsx by catching chunk-load failures
  // that escape the React tree (Vite's `vite:preloadError`, unhandled
  // rejections, window errors) and doing a single loop-guarded reload onto
  // the now-consistent build. Pairs with the soft `apex:version-drift` toast
  // below — that one prompts; this one rescues an actual missing chunk.
  useEffect(() => installChunkErrorReload(), []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          <AppShell />
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
